<script>
  import Button from '@components/buttons/button.svelte'
  import Text from '@components/general/text.svelte'
  import Modal from '@components/general/modal.svelte'
  import { useForm, page } from '@inertiajs/svelte'
  import { get } from '@rails/request.js'
  import { CircleCheck, CircleAlert, Lock } from 'lucide-svelte'
  import { cn } from '@lib/utils'

  let { session, size = "sm" } = $props()

  let user = $derived($page.props.user)
  let isAdmin = $derived(user?.is_admin)
  let canEnroll = $derived(session?.id && user?.has_active_membership && !isAdmin)

  let loading = $state(false)
  let error = $state(null)
  let enrolled = $state(false)

  let showPreview = $state(false)
  let showConfirm = $state(false)

  let confirmForm = useForm()
  let cancelForm = useForm()

  let buttonClass = $derived(cn(
    'flex items-center gap-1 bg-white text-gray-700 shadow-xs rounded-sm hover:shadow-sm hover:text-gray-800',
    size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-1.5 py-1 text-xs'
  ))

  async function checkRegistration() {
    try {
      loading = true
      const response = await get(`/api/v1/live_sessions/${session.id}/user_registration`, { responseKind: 'json' })
      const data = await response.json
      if (response.ok) {
        enrolled = data?.enrolled || false
      } else {
        error = data
      }
    } catch (e) {
      error = e.message
    } finally {
      loading = false
    }
  }

  function handleClick(e) {
    e.stopPropagation()
    e.preventDefault()
    if (enrolled) {
      showPreview = true
    } else {
      showConfirm = true
    }
  }

  function handleEnroll() {
    $confirmForm.post(`/live-sessions/${session.id}/register`, {
      onSuccess: () => {
        loading = false
        error = null
        enrolled = true
        showConfirm = false
      }
    })
  }

  function handleCancel() {
    if (!window.confirm("Are you sure you want to cancel your enrollment?")) return
    $cancelForm.delete(`/live-sessions/${session.id}/unregister`, { 
      onSuccess: () => {
        loading = false
        error = null
        enrolled = false
        showPreview = false
      }
    })
  }

  let buttonProps = $derived.by(() => {
    if (user.is_admin) return { text: "Not enrolled", Icon: CircleAlert, disabled: true, hint: "Admins cannot enroll in live sessions" }
    if (canEnroll) return { text: loading ? "..." : enrolled ? "Enrolled" : "Not enrolled", Icon: loading ? null : enrolled ? CircleCheck : CircleAlert, loading: loading, onclick: handleClick }
    return { text: "Premium only", Icon: Lock, hint: "Upgrade your membership to enroll in live sessions", path: "/register" }
  })

  $effect(async () => {
    if (canEnroll) await checkRegistration()
  })
</script>

<Button variant="naked" class={buttonClass} tooltipProps={{ side: "right" }} {...buttonProps} />

<!-- Preview existing enrollment -->
<Modal bind:open={showPreview} title="Enrolled in session" width="40rem" onClose={() => showPreview = false}>
  <Text text="You are enrolled in this session" />
  {#snippet footer()}
    <Button variant="warning" text="Cancel enrollment" onclick={handleCancel} loading={$cancelForm.processing} />
  {/snippet}
</Modal>

<!-- Confirm new enrollment -->
<Modal bind:open={showConfirm} title={`Confirm enrollment for ${session.event_name}`} width="40rem">
  <Text text="Are you sure you want to enroll in this session?" class="text-sm" />
  {#snippet footer()}
    <Button variant="confirm" text="Enroll" onclick={handleEnroll} Icon={CircleCheck} loading={$confirmForm.processing} />
  {/snippet}
</Modal>
